import React, { useMemo, useState, useEffect } from 'react';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { UsageWarningBanner } from './UsageWarningBanner';

interface UsageLimitBannerProps {
  onUpgrade: () => void;
}

type LimitType = 'contentUploads' | 'flashcards' | 'chatMessages' | 'quizAnswers' | 'practiceExams';

interface LimitConfig {
  key: LimitType;
  label: string;
  mobileLabel: string;
  period: 'daily' | 'monthly';
}

const LIMIT_CONFIGS: LimitConfig[] = [
  { key: 'chatMessages', label: 'AI chats', mobileLabel: 'chats', period: 'daily' },
  { key: 'contentUploads', label: 'uploads', mobileLabel: 'uploads', period: 'daily' },
  { key: 'flashcards', label: 'flashcard generations', mobileLabel: 'flashcards', period: 'daily' },
  { key: 'quizAnswers', label: 'quiz answers', mobileLabel: 'quizzes', period: 'daily' },
  { key: 'practiceExams', label: 'practice exams', mobileLabel: 'exams', period: 'monthly' },
];

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return '';
  
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function useCountdown(targetDate: string | null): string {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!targetDate) {
      setTimeRemaining('');
      return;
    }

    const calculateRemaining = () => {
      const now = Date.now();
      const target = new Date(targetDate).getTime();
      const diff = target - now;
      setTimeRemaining(formatTimeRemaining(diff));
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [targetDate]);

  return timeRemaining;
}

export const UsageLimitBanner: React.FC<UsageLimitBannerProps> = ({ onUpgrade }) => {
  const usage = useUsageLimits();

  const criticalLimit = useMemo(() => {
    // Don't show banner for Pro/Premium users or while loading
    if (usage.isLoading || !usage.isFreeTier) return null;

    let highest: { config: LimitConfig; percentage: number; used: number; limit: number } | null = null;

    for (const config of LIMIT_CONFIGS) {
      const { used, limit } = usage[config.key];
      if (limit === 0) continue;
      
      const percentage = (used / limit) * 100;
      
      // Only show banner for 80%+ usage
      if (percentage >= 80) {
        if (!highest || percentage > highest.percentage) {
          highest = { config, percentage, used, limit };
        }
      }
    }

    return highest;
  }, [usage]);

  // Get the appropriate reset time based on the limit period
  const resetTime = criticalLimit?.config.period === 'monthly' 
    ? usage.monthlyResetAt 
    : usage.dailyResetAt;
  
  const countdown = useCountdown(resetTime);

  if (!criticalLimit) return null;

  const { config, percentage, used, limit } = criticalLimit;

  // Determine variant based on percentage
  let variant: 'info' | 'warning' | 'error';
  if (percentage >= 100) {
    variant = 'error';
  } else if (percentage >= 90) {
    variant = 'warning';
  } else {
    variant = 'info';
  }

  // Generate messages with countdown
  const isAtLimit = percentage >= 100;
  const countdownText = countdown ? ` Resets in ${countdown}.` : '';
  
  const message = isAtLimit
    ? `You've reached your ${config.label} limit.${countdownText} Upgrade for unlimited access.`
    : `You've used ${used} of ${limit} ${config.label}.${countdownText} Upgrade for unlimited.`;

  const mobileCountdown = countdown ? ` (${countdown})` : '';
  const mobileMessage = isAtLimit
    ? `Limit reached${mobileCountdown}. Upgrade.`
    : `${used}/${limit} used${mobileCountdown}. Upgrade.`;

  // Create storage key that resets appropriately
  const today = new Date().toISOString().split('T')[0];
  const month = today.slice(0, 7);
  const resetKey = config.period === 'daily' ? today : month;
  const storageKey = `usage-banner-dismissed-${config.key}-${resetKey}-${Math.floor(percentage / 10) * 10}`;

  return (
    <UsageWarningBanner
      variant={variant}
      message={message}
      mobileMessage={mobileMessage}
      actionLabel="Upgrade now"
      mobileActionLabel="Upgrade"
      onAction={onUpgrade}
      dismissible={true}
      storageKey={storageKey}
    />
  );
};
